// Canonical dotted-version comparer for the dashboard client — the ONE place version strings
// get parsed/compared in TS. Mirrors Shared.Helpers.FirmwareHelpers.CompareVersions on the
// server (same semantics: numeric per-part comparison, variable length, missing/non-numeric
// parts treated as 0) so the client and server always rank versions identically.
//
// "2.0.10.4" ranks ahead of "2.0.9.3" — a plain string/lexicographic compare gets this wrong
// ("10" < "9"), which was a real bug in one of this file's former call sites.
const numOrZero = (part: string | undefined): number => {
    const n = Number(part);
    return Number.isFinite(n) ? n : 0;
};

export const compareVersions = (v1?: string, v2?: string): number => {
    const parts1 = (v1 ?? "").split(".");
    const parts2 = (v2 ?? "").split(".");
    const len = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < len; i++) {
        const a = numOrZero(parts1[i]);
        const b = numOrZero(parts2[i]);
        if (a !== b) return a - b;
    }
    return 0;
};

// Named counterpart for descending call sites (newest-first lists) — reads clearer than
// swapping argument order at the call site.
export const compareVersionsDesc = (v1?: string, v2?: string): number => compareVersions(v2, v1);

export const hasMediaRequests = (firmwareVersion?: string) => compareVersions(firmwareVersion, "2.0.3.0") >= 0;
export const hasVideoRecompress = (firmwareVersion?: string) => compareVersions(firmwareVersion, "2.0.5.0") >= 0;
export const hasLaneCollection = (firmwareVersion?: string) => compareVersions(firmwareVersion, "2.0.9.0") >= 0;
export const DRAKEWELL_ENDPOINT_FIRMWARE_VERSION = "2.0.9.6";
export const hasDrakewellEndpoints = (firmwareVersion?: string) => compareVersions(firmwareVersion, DRAKEWELL_ENDPOINT_FIRMWARE_VERSION) >= 0;

// First firmware that handles TestEndpointCommand (the relay/endpoint test). Mirrors
// Shared.Helpers.FirmwareHelpers.TEST_ENDPOINT_FIRMWARE_VERSION — keep in sync. Exported so the
// UI can name the version it requires instead of repeating the literal in a label.
export const TEST_ENDPOINT_FIRMWARE_VERSION = "2.0.8.0";

// First firmware that refreshes its own device photo on a schedule (DevicePhotoUpdateIntervalMinutes).
// Mirrors Shared.Helpers.FirmwareHelpers.SELF_SCHEDULED_DEVICE_PHOTO_FIRMWARE_VERSION — keep in sync.
// Below it the setting has no effect: the server asks the unit for a photo every two hours instead.
export const SELF_SCHEDULED_DEVICE_PHOTO_FIRMWARE_VERSION = "2.0.11.5";
export const hasEndpointTest = (firmwareVersion?: string) => compareVersions(firmwareVersion, TEST_ENDPOINT_FIRMWARE_VERSION) >= 0;

// First firmware whose WebRelayModel.Auto detection can be trusted. 2.0.11.3 (in production as of
// October 2026) shipped detection too, but judged the fleet's 400-series quads wrong, so it must not
// clear the gate; the server hands every unit below it Quad (400 series) in place of Auto
// (DeviceEndpointCompatibility). Mirrors Shared.Helpers.FirmwareHelpers.WEB_RELAY_AUTO_DETECT_FIRMWARE_VERSION
// — keep in sync. Exported so the endpoint editor can name the version instead of repeating the literal.
export const WEB_RELAY_AUTO_DETECT_FIRMWARE_VERSION = "2.0.11.4";
export const hasWebRelayAutoDetect = (firmwareVersion?: string) => compareVersions(firmwareVersion, WEB_RELAY_AUTO_DETECT_FIRMWARE_VERSION) >= 0;
